import { supabase } from "@/lib/supabase";

export const chatThreadsService = {
  async listThreads(organizationId: string, instanceId?: string) {
    let q = supabase
      .from("chat_threads")
      .select(
        "id, assistant_instance_id, title, created_at, updated_at, selected_tools",
      )
      .eq("organization_id", organizationId)
      .order("updated_at", { ascending: false });
    if (instanceId) q = q.eq("assistant_instance_id", instanceId);
    const { data, error } = await q;
    return { data, error } as const;
  },

  async listMessages(threadId: string, limit = 50, offset = 0) {
    const { data, error } = await supabase
      .from("chat_messages")
      .select("id, role, content, parts, created_at")
      .eq("thread_id", threadId)
      .order("created_at", { ascending: true })
      .range(offset, offset + limit - 1);
    return { data, error } as const;
  },

  async createThread(
    organizationId: string,
    assistantInstanceId?: string | null,
    title?: string | null,
    userId?: string | null,
  ) {
    const { data, error } = await supabase
      .from("chat_threads")
      .insert({
        organization_id: organizationId,
        assistant_instance_id: assistantInstanceId || null,
        title: title || null,
        user_id: userId || null,
      })
      .select("id")
      .single();
    return { data, error } as const;
  },

  async updateThreadTitle(threadId: string, title: string) {
    const { data, error } = await supabase
      .from("chat_threads")
      .update({ title })
      .eq("id", threadId)
      .select("id, title")
      .single();
    return { data, error } as const;
  },

  // Convert database messages to AI SDK UI format with parts support
  convertMessagesToUIFormat(messages: any[]) {
    return messages.map((msg, index) => ({
      id: msg.id || `msg-${index}`,
      role: msg.role,
      // Use parts if available (AI SDK v5 structure), fallback to content for backward compatibility
      parts:
        msg.parts && Array.isArray(msg.parts)
          ? msg.parts
          : [
              {
                type: "text" as const,
                text: msg.content || "",
              },
            ],
      createdAt: new Date(msg.created_at),
    }));
  },

  async getThread(threadId: string) {
    const { data, error } = await supabase
      .from("chat_threads")
      .select("*")
      .eq("id", threadId)
      .single();

    if (error) {
      console.error("Error fetching chat thread:", error);
      return { data: null, error };
    }

    return { data, error: null };
  },

  // Load messages for a thread and convert to UI format
  async loadChatMessages(threadId: string) {
    const { data: messages, error } = await this.listMessages(threadId, 200);

    if (error || !messages) {
      console.error("Error loading chat messages:", error);
      return { data: [], error };
    }

    const uiMessages = this.convertMessagesToUIFormat(messages);
    return { data: uiMessages, error: null };
  },
};
